
import React, { useState, useEffect, useMemo } from 'react';
import { UserProfile, Expert } from '../../../types';
import { Loader2, Star, User, Send, ChevronDown, ChevronUp, Trash2, Plus, X, Sparkles, CheckSquare, Square } from 'lucide-react';
import CommentGeneratorModal from './CommentGeneratorModal';

interface Comment {
    id: number;
    target_type: string;
    target_id: number;
    target_name: string;
    user_id: number;
    user_name: string;
    user_avatar: string | null;
    is_expert: number;
    comment: string;
    star: number | null;
    reply_id: number | null;
    created_at: string;
}

interface Props {
    currentUser: UserProfile;
    experts: Expert[];
}

const NewCommentForm: React.FC<{
    bots: UserProfile[];
    articles: { id: number; title: string }[];
    experts: { rowid: number; name: string }[];
    onSubmit: (payload: {
        target_type: 'article' | 'expert';
        target_id: number;
        user_id: number;
        comment: string;
        reply_id: null;
        star: number | null;
        created_at: string;
    }) => Promise<void>;
}> = ({ bots, articles, experts, onSubmit }) => {
    const [targetType, setTargetType] = useState<'article' | 'expert'>('article');
    const [targetId, setTargetId] = useState<number | ''>('');
    const [userId, setUserId] = useState<number | ''>('');
    const [comment, setComment] = useState('');
    const [star, setStar] = useState<string>('');
    const [createdAt, setCreatedAt] = useState(new Date().toISOString().slice(0, 16));
    const [isSubmitting, setIsSubmitting] = useState(false);

    const targetList = useMemo(() => {
        if (targetType === 'article') return articles.map(a => ({ id: a.id, name: a.title }));
        if (targetType === 'expert') return experts.map(e => ({ id: e.rowid, name: e.name }));
        return [];
    }, [targetType, articles, experts]);

    useEffect(() => {
        setTargetId(''); // Reset target when type changes
    }, [targetType]);
    
    useEffect(() => {
        if (bots.length > 0 && !userId) {
            setUserId(bots[0].id!);
        }
    }, [bots, userId]);


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!targetType || !targetId || !userId || !comment) return;

        setIsSubmitting(true);
        await onSubmit({
            target_type: targetType,
            target_id: targetId,
            user_id: userId,
            comment: comment,
            reply_id: null,
            star: star ? Number(star) : null,
            created_at: new Date(createdAt).toISOString()
        });
        
        setComment('');
        setStar('');
        setCreatedAt(new Date().toISOString().slice(0, 16));
        setIsSubmitting(false);
    };

    return (
        <form onSubmit={handleSubmit} className="bg-mystic-box border border-gray-800 p-4 rounded-lg space-y-3 mb-6 animate-fade-in">
             <h3 className="font-bold text-white">Add New Comment</h3>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <select value={targetType} onChange={e => setTargetType(e.target.value as 'article' | 'expert')} className="bg-mystic-inset border border-gray-700 rounded p-2 text-xs h-9 w-full">
                    <option value="article">Article</option>
                    <option value="expert">Expert</option>
                </select>
                <select value={targetId} onChange={e => setTargetId(Number(e.target.value))} className="bg-mystic-inset border border-gray-700 rounded p-2 text-xs h-9 w-full" disabled={targetList.length === 0} required>
                    <option value="">-- Select Target --</option>
                    {targetList.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
             </div>
             <div className="flex items-start gap-2 flex-wrap">
                <select value={userId} onChange={e => setUserId(Number(e.target.value))} className="bg-mystic-inset border border-gray-700 rounded p-1.5 text-xs h-9" required>
                    {bots.map(bot => <option key={bot.id} value={bot.id}>{bot.name}</option>)}
                </select>
                <textarea
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    placeholder="Comment text..."
                    className="flex-1 bg-mystic-inset border border-gray-700 p-2 rounded text-white text-xs h-9 resize-none min-w-[200px]"
                    rows={1}
                    required
                />
                <select value={star} onChange={e => setStar(e.target.value)} className="bg-mystic-inset border border-gray-700 rounded p-1.5 text-xs h-9">
                    <option value="">No rating</option>
                    {[1, 2, 3, 4, 5].map(s => <option key={s} value={s}>{s} Star{s > 1 ? 's' : ''}</option>)}
                </select>
                <input type="datetime-local" value={createdAt} onChange={e => setCreatedAt(e.target.value)} className="bg-mystic-inset border border-gray-700 rounded p-1.5 text-xs h-9 text-gray-300"/>
                <button type="submit" disabled={isSubmitting} className="bg-mystic-accent text-black p-2 rounded h-9 w-9 flex items-center justify-center disabled:opacity-50">
                    {isSubmitting ? <Loader2 className="animate-spin w-4 h-4" /> : <Send className="w-4 h-4" />}
                </button>
            </div>
        </form>
    );
};


const ReplyForm: React.FC<{
    value: string;
    onChange: (value: string) => void;
    bots: UserProfile[];
    experts: Expert[];
    parentComment: Comment;
    onSubmit: (payload: {
        target_type: string;
        target_id: number;
        user_id: number;
        comment: string;
        reply_id: number;
        star: number | null;
        created_at: string;
    }) => Promise<void>;
    allCommentsInThread: Comment[];
}> = ({ value, onChange, bots, experts, parentComment, onSubmit, allCommentsInThread }) => {
    const [selectedUserId, setSelectedUserId] = useState<number | undefined>();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [replyDate, setReplyDate] = useState('');
    const [starRating, setStarRating] = useState<string>('');
    
    const targetExpert = useMemo(() => {
        if (parentComment.target_type === 'expert') {
            return experts.find(e => e.rowid === parentComment.target_id);
        }
        return null;
    }, [experts, parentComment]);

    const expertUsers = useMemo(() => {
        return experts
            .filter(e => e.user_id != null)
            .map(e => ({ id: e.user_id!, name: e.name }));
    }, [experts]);
    
    const { usedBots, otherBots } = useMemo(() => {
        const usedBotIds = new Set<number>();
        allCommentsInThread.forEach(c => {
            if (bots.some(b => b.id === c.user_id)) {
                usedBotIds.add(c.user_id);
            }
        });

        const used = bots.filter(b => usedBotIds.has(b.id!));
        const other = bots.filter(b => !usedBotIds.has(b.id!));
        return { usedBots: used, otherBots: other };
    }, [bots, allCommentsInThread]);

    useEffect(() => {
        if (targetExpert && targetExpert.user_id) {
            setSelectedUserId(targetExpert.user_id);
        } else {
            const firstAvailableBot = usedBots[0] || otherBots[0] || expertUsers[0];
            if(firstAvailableBot) setSelectedUserId(firstAvailableBot.id);
        }
    }, [targetExpert, usedBots, otherBots, expertUsers]);

    const calculateRandomReplyDate = (baseDateString: string) => {
        const parentDate = new Date(baseDateString);
        const minOffset = 60 * 1000; // 1 minute
        const maxOffset = 7 * 24 * 60 * 60 * 1000; // 7 days
        const randomOffset = minOffset + Math.random() * (maxOffset - minOffset);
        const randomReplyDate = new Date(parentDate.getTime() + randomOffset);
        
        const tzoffset = (new Date()).getTimezoneOffset() * 60000;
        const localISOTime = (new Date(randomReplyDate.getTime() - tzoffset)).toISOString().slice(0, 16);
        
        return localISOTime;
    };

    useEffect(() => {
        setReplyDate(calculateRandomReplyDate(parentComment.created_at));
    }, [parentComment.id]);

    const allUsers = useMemo(() => [...expertUsers, ...bots], [expertUsers, bots]);
    const selectedUserName = useMemo(() => {
        return allUsers.find(u => u.id === selectedUserId)?.name || '';
    }, [selectedUserId, allUsers]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!value.trim() || !selectedUserId) return;

        setIsSubmitting(true);
        await onSubmit({
            target_type: parentComment.target_type,
            target_id: parentComment.target_id,
            user_id: selectedUserId,
            comment: value,
            reply_id: parentComment.id,
            star: starRating ? Number(starRating) : null,
            created_at: new Date(replyDate).toISOString()
        });
        setReplyDate(calculateRandomReplyDate(parentComment.created_at));
        setStarRating('');
        setIsSubmitting(false);
    };

    return (
        <form onSubmit={handleSubmit} className="flex items-start gap-2 mt-3 flex-wrap">
            <select
                value={selectedUserId || ''}
                onChange={e => setSelectedUserId(Number(e.target.value))}
                className="bg-mystic-inset border border-gray-700 rounded p-1.5 text-xs h-9"
            >
                {targetExpert && targetExpert.user_id && (
                     <optgroup label="Target Expert">
                        <option value={targetExpert.user_id}>{targetExpert.name}</option>
                    </optgroup>
                )}
                {expertUsers.filter(e => e.id !== targetExpert?.user_id).length > 0 && (
                     <optgroup label="Other Experts">
                        {expertUsers
                            .filter(e => e.id !== targetExpert?.user_id)
                            .map(expert => (
                                <option key={`expert-${expert.id}`} value={expert.id}>{expert.name}</option>
                            ))
                        }
                    </optgroup>
                )}
                {usedBots.length > 0 && (
                    <optgroup label="Bots Used Here">
                        {usedBots.map(bot => <option key={bot.id} value={bot.id}>{bot.name}</option>)}
                    </optgroup>
                )}
                 {otherBots.length > 0 && (
                    <optgroup label="Other Bots">
                        {otherBots.map(bot => <option key={bot.id} value={bot.id}>{bot.name}</option>)}
                    </optgroup>
                )}
            </select>
            <textarea
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={`Reply as ${selectedUserName}...`}
                className="flex-1 bg-mystic-inset border border-gray-700 p-2 rounded text-white text-xs h-9 resize-none min-w-[200px]"
                rows={1}
            />
            <select
                value={starRating}
                onChange={e => setStarRating(e.target.value)}
                className="bg-mystic-inset border border-gray-700 rounded p-1.5 text-xs h-9"
            >
                <option value="">No rating</option>
                <option value="1">1 Star</option>
                <option value="2">2 Stars</option>
                <option value="3">3 Stars</option>
                <option value="4">4 Stars</option>
                <option value="5">5 Stars</option>
            </select>
            <input 
                type="datetime-local" 
                value={replyDate} 
                onChange={e => setReplyDate(e.target.value)} 
                className="bg-mystic-inset border border-gray-700 rounded p-1.5 text-xs h-9 text-gray-300" 
            />
            <button type="submit" disabled={isSubmitting} className="bg-mystic-accent text-black p-2 rounded h-9 w-9 flex items-center justify-center disabled:opacity-50">
                {isSubmitting ? <Loader2 className="animate-spin w-4 h-4" /> : <Send className="w-4 h-4" />}
            </button>
        </form>
    );
};

const CommentNode: React.FC<{
    comment: Comment & { replies: Comment[] };
    bots: UserProfile[];
    experts: Expert[];
    onSubmit: (payload: any) => Promise<void>;
    onDelete: (commentId: number) => Promise<void>;
    allCommentsInThread: Comment[];
    level: number;
    selectedIds: Set<number>;
    onSelect: (id: number) => void;
    replyTexts: Record<number, string>;
    onReplyTextChange: (id: number, text: string) => void;
}> = ({ comment, bots, experts, onSubmit, onDelete, allCommentsInThread, level, selectedIds, onSelect, replyTexts, onReplyTextChange }) => {
    const isSelected = selectedIds.has(comment.id);
    const currentReplyText = replyTexts[comment.id] || '';

    return (
        <div className={`flex items-start gap-3 ${level > 0 ? 'mt-4' : ''}`}>
            <div className="flex flex-col items-center gap-2 pt-1">
                <div onClick={() => onSelect(comment.id)} className="cursor-pointer">
                    {isSelected ? <CheckSquare className="w-4 h-4 text-mystic-accent"/> : <Square className="w-4 h-4 text-gray-600"/>}
                </div>
                {comment.user_avatar ? (
                    <img src={comment.user_avatar} alt={comment.user_name} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-gray-400" />
                    </div>
                )}
            </div>
            <div className="flex-1 group">
                <div className="flex items-center justify-between gap-x-2 flex-wrap">
                    <div className="flex items-center gap-2">
                        <p className="font-bold text-white text-sm">{comment.user_name}</p>
                        {comment.star && (
                            <div className="flex items-center gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className={`w-3 h-3 ${i < comment.star! ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`} />
                                ))}
                            </div>
                        )}
                    </div>
                     <div className="flex items-center gap-2">
                        <p className="text-xs text-gray-500 shrink-0">{new Date(comment.created_at).toLocaleString('pl-PL')}</p>
                        <button onClick={() => onDelete(comment.id)} className="text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-1">
                            <Trash2 className="w-3.5 h-3.5" />
                        </button>
                    </div>
                </div>
                <p className="text-gray-300 mt-1 text-sm">{comment.comment}</p>

                <ReplyForm 
                    value={currentReplyText}
                    onChange={(text) => onReplyTextChange(comment.id, text)}
                    bots={bots} 
                    experts={experts} 
                    parentComment={comment} 
                    onSubmit={onSubmit} 
                    allCommentsInThread={allCommentsInThread} 
                />
                
                {comment.replies && comment.replies.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-800/50">
                        {comment.replies.map((reply: Comment) => (
                            <CommentNode 
                                key={reply.id} 
                                comment={reply} 
                                bots={bots} 
                                experts={experts} 
                                onSubmit={onSubmit} 
                                onDelete={onDelete} 
                                allCommentsInThread={allCommentsInThread} 
                                level={level + 1} 
                                selectedIds={selectedIds}
                                onSelect={onSelect}
                                replyTexts={replyTexts}
                                onReplyTextChange={onReplyTextChange}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};


const CommentsManager: React.FC<Props> = ({ currentUser, experts }) => {
    const [comments, setComments] = useState<Comment[]>([]);
    const [bots, setBots] = useState<UserProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [openTargets, setOpenTargets] = useState<Set<string>>(new Set());
    
    const [showNewCommentForm, setShowNewCommentForm] = useState(false);
    const [showGeneratorModal, setShowGeneratorModal] = useState(false);
    const [articlesForSelect, setArticlesForSelect] = useState<{id: number, title: string}[]>([]);
    
    // State for AI replies
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [isGeneratingReplies, setIsGeneratingReplies] = useState(false);
    const [replyTexts, setReplyTexts] = useState<Record<number, string>>({});


    const fetchComments = async () => {
        try {
            const res = await fetch('/api/cms/comments', { headers: { 'x-admin-phone': currentUser.phone } });
            if (res.ok) setComments(await res.json());
        } catch (e) { console.error("Failed to fetch comments", e); }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await fetchComments();
            try {
                const usersRes = await fetch('/api/users', { headers: { 'x-admin-phone': currentUser.phone } });
                if (usersRes.ok) {
                    const allUsers: UserProfile[] = await usersRes.json();
                    setBots(allUsers.filter(u => u.rank === 3));
                }

                const articlesRes = await fetch('/api/cms/blog?limit=200', { headers: { 'x-admin-phone': currentUser.phone } });
                if (articlesRes.ok) {
                    const articlesData = await articlesRes.json();
                    setArticlesForSelect(articlesData.data || []);
                }

            } catch (e) { console.error("Failed to fetch auxiliary data", e); }
            setLoading(false);
        };
        fetchData();
    }, [currentUser.phone]);

    const groupedAndSortedComments = useMemo(() => {
        const groups: { [key: string]: Comment[] } = {};
        comments.forEach(comment => {
            const key = `${comment.target_type}:${comment.target_id}`;
            if (!groups[key]) {
                groups[key] = [];
            }
            groups[key].push(comment);
        });
    
        return Object.entries(groups).sort(([, commentsA], [, commentsB]) => {
            const latestA = commentsA[0] ? new Date(commentsA[0].created_at).getTime() : 0;
            const latestB = commentsB[0] ? new Date(commentsB[0].created_at).getTime() : 0;
            return latestB - latestA;
        });
    }, [comments]);
    
    const handleReplyTextChange = (id: number, text: string) => {
        setReplyTexts(prev => ({ ...prev, [id]: text }));
    };

    const handleCommentSubmit = async (payload: any) => {
        try {
            const res = await fetch('/api/cms/comments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                await fetchComments();
                 if (payload.reply_id) {
                    handleReplyTextChange(payload.reply_id, '');
                }
            } else {
                throw new Error("Failed to post comment");
            }
        } catch (e) {
            console.error(e);
            alert("Error submitting comment.");
        }
    };

    const handleGenerateReplies = async () => {
        setIsGeneratingReplies(true);
        const repliesToGenerate = Array.from(selectedIds)
            .map(id => comments.find(c => c.id === id))
            .filter((c): c is Comment => !!c);
    
        for (const parentComment of repliesToGenerate) {
            const expert = experts.find(e => e.rowid === parentComment.target_id && parentComment.target_type === 'expert');
            if (!expert || !expert.user_id) continue;
    
            try {
                const res = await fetch('/api/cms/generate', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        type: 'comment_reply',
                        parentCommentContent: parentComment.comment,
                        expertName: expert.name,
                        expertBio: expert.bio,
                        expertDescription: expert.description,
                    }),
                });
    
                if (!res.ok) throw new Error("AI API failed");
    
                const { replyContent } = await res.json();
                if (replyContent) {
                    handleReplyTextChange(parentComment.id, replyContent);
                }
            } catch (error) {
                console.error(`Failed to generate reply for comment ${parentComment.id}:`, error);
            }
        }
    
        setIsGeneratingReplies(false);
        setSelectedIds(new Set());
    };
    
    const handlePublishingComplete = async () => {
        await fetchComments();
    };

    const handleDeleteComment = async (commentId: number) => {
        if (!confirm(`Are you sure you want to delete comment #${commentId}? This will also delete all its replies.`)) return;
    
        try {
            const res = await fetch('/api/cms/comments', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json', 'x-admin-phone': currentUser.phone },
                body: JSON.stringify({ ids: [commentId] })
            });
            if (res.ok) {
                await fetchComments();
            } else {
                throw new Error("Failed to delete comment");
            }
        } catch (e) {
            console.error(e);
            alert("Error deleting comment.");
        }
    };
    
    const toggleTarget = (key: string) => {
        setOpenTargets(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
        setSelectedIds(new Set());
    };

    if (loading) return <div className="flex justify-center p-8"><Loader2 className="animate-spin w-8 h-8 text-gray-400" /></div>;

    return (
        <div className="space-y-4">
            <div className="mb-4 flex gap-4 items-center">
                <button
                    onClick={() => setShowNewCommentForm(prev => !prev)}
                    className="flex items-center gap-2 text-sm font-bold text-gray-300 hover:text-white bg-mystic-btn hover:bg-mystic-btnHover px-4 py-2 rounded-lg transition-colors"
                >
                    {showNewCommentForm ? <X className="w-4 h-4"/> : <Plus className="w-4 h-4"/>}
                    Add New Comment
                </button>
                 <button
                    onClick={() => setShowGeneratorModal(true)}
                    className="flex items-center gap-2 text-sm font-bold text-mystic-accent hover:text-white bg-mystic-accent/10 hover:bg-mystic-accent/20 px-4 py-2 rounded-lg transition-colors border border-mystic-accent/30"
                >
                    <Sparkles className="w-4 h-4"/>
                    Generate Comments
                </button>
            </div>
            
             {selectedIds.size > 0 && (
                <div className="fixed bottom-8 left-8 z-50 animate-fade-in">
                    <button
                        onClick={handleGenerateReplies}
                        disabled={isGeneratingReplies}
                        className="flex items-center gap-3 text-base font-bold text-black bg-mystic-accent hover:bg-mystic-accentHover px-6 py-4 rounded-full transition-all shadow-lg hover:shadow-xl shadow-mystic-accent/20 disabled:opacity-50"
                    >
                        {isGeneratingReplies ? <Loader2 className="w-5 h-5 animate-spin"/> : <Sparkles className="w-5 h-5"/>}
                        Generate AI Replies
                        <span className="bg-black/20 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">{selectedIds.size}</span>
                    </button>
                </div>
            )}

            {showGeneratorModal && (
                <CommentGeneratorModal 
                    isOpen={showGeneratorModal}
                    onClose={() => setShowGeneratorModal(false)}
                    currentUser={currentUser}
                    bots={bots}
                    articles={articlesForSelect}
                    experts={experts}
                    allComments={comments}
                    onPublish={handlePublishingComplete}
                />
            )}

            {showNewCommentForm && (
                <NewCommentForm
                    bots={bots}
                    articles={articlesForSelect}
                    experts={experts}
                    onSubmit={handleCommentSubmit}
                />
            )}

            {groupedAndSortedComments.map(([key, commentList]) => {
                const targetName = commentList[0]?.target_name || 'Unknown';
                const targetId = commentList[0]?.target_id;
                const targetType = commentList[0]?.target_type;
                const isOpen = openTargets.has(key);

                const buildTree = (list: Comment[], parentId: number | null = null): (Comment & { replies: Comment[] })[] => {
                    return list
                        .filter(comment => comment.reply_id === parentId)
                        .map(comment => ({
                            ...comment,
                            replies: buildTree(list, comment.id)
                        }));
                };

                const commentTree = buildTree(commentList);

                return (
                    <div key={key} className="bg-mystic-box border border-gray-800 rounded-lg">
                        <button onClick={() => toggleTarget(key)} className="w-full flex justify-between items-center p-4 text-left">
                            <h3 className="font-bold text-white">
                                <span className="uppercase text-xs bg-gray-700 px-2 py-1 rounded-full mr-2">{targetType}</span>
                                {targetName} <span className="text-gray-500">#{targetId}</span>
                            </h3>
                            <div className="flex items-center gap-2 text-gray-400">
                                <span className="text-xs">{commentList.length} comments</span>
                                {isOpen ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
                            </div>
                        </button>
                        
                        {isOpen && (
                            <div className="p-4 border-t border-gray-800 space-y-6">
                                {commentTree.map(rootComment => (
                                    <CommentNode 
                                        key={rootComment.id} 
                                        comment={rootComment} 
                                        bots={bots} 
                                        experts={experts}
                                        onSubmit={handleCommentSubmit}
                                        onDelete={handleDeleteComment}
                                        allCommentsInThread={commentList}
                                        level={0}
                                        selectedIds={selectedIds}
                                        onSelect={(id) => setSelectedIds(prev => {
                                            const newSet = new Set(prev);
                                            if (newSet.has(id)) newSet.delete(id);
                                            else newSet.add(id);
                                            return newSet;
                                        })}
                                        replyTexts={replyTexts}
                                        onReplyTextChange={handleReplyTextChange}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default CommentsManager;
